import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, ShieldCheck } from "lucide-react";
import FadeUp from "../components/FadeUp";
import FeaturedProject from "../components/FeaturedProject";
import { api } from "../lib/api";
import { STOCK, COMMERCIAL_GALLERY } from "../lib/stock";
import { FEATURED_PROJECT_COMMERCIAL } from "../lib/constants";

const SCOPES = [
    "Ground-up commercial construction",
    "Office, retail & restaurant build-outs",
    "Tenant improvements & finish-outs",
    "Structural modifications & additions",
    "Electrical, HVAC, plumbing (MEP)",
    "TPO / PVC / flat roofing systems",
    "Concrete, foundations & site work",
    "Interior finishes & painting",
];

export default function CommercialProjects() {
    const [photos, setPhotos] = useState([]);

    useEffect(() => {
        api.get("/photos", { params: { section: "commercial" } })
            .then((r) => setPhotos(r.data || []))
            .catch(() => setPhotos([]));
    }, []);

    return (
        <main data-testid="commercial-page" className="bg-[#050505]">
            {/* HERO */}
            <section className="relative pt-40 pb-24 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={STOCK.commercialHero}
                        alt=""
                        aria-hidden="true"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/30" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-transparent to-[#050505]" />
                </div>
                <div className="container-bevel relative z-10">
                    <FadeUp>
                        <span className="overline">Commercial Projects</span>
                    </FadeUp>
                    <FadeUp delay={1}>
                        <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95] max-w-4xl">
                            Commercial work managed with{" "}
                            <span className="text-[#F5A623]">
                                technical precision.
                            </span>
                        </h1>
                    </FadeUp>
                    <FadeUp delay={2}>
                        <p className="mt-8 text-lg text-white/65 max-w-2xl leading-relaxed">
                            From tenant finish-outs to full commercial
                            renovations. Bevel manages every phase from
                            blueprint to handover, ensuring precise execution
                            for businesses, developers, and operators.
                        </p>
                    </FadeUp>
                    <FadeUp delay={3}>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Link
                                to="/contact"
                                data-testid="commercial-hero-cta"
                                className="btn-primary"
                            >
                                Start a Commercial Project{" "}
                                <ArrowRight size={16} />
                            </Link>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* FEATURED PROJECT (moved to top) */}
            <section
                className="section-pad bg-[#080808] hairline-top"
                data-testid="commercial-featured-project-section"
            >
                <div className="container-bevel">
                    <FadeUp>
                        <FeaturedProject
                            testId="commercial-featured-project"
                            eyebrow="Featured Project"
                            title="Domino's Tulsa, Commercial Build"
                            location="Tulsa, OK"
                            description="A commercial build managed end-to-end by Bevel. Tight schedule, clear stakeholder reporting, and full-cycle oversight from blueprint through final inspection."
                            image={FEATURED_PROJECT_COMMERCIAL}
                            cta={{
                                label: "Start a Commercial Project",
                                to: "/contact",
                            }}
                        />
                    </FadeUp>
                </div>
            </section>

            {/* SCOPE GRID */}
            <section
                className="section-pad"
                data-testid="commercial-scope-section"
            >
                <div className="container-bevel grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <FadeUp>
                            <span className="overline">What We Build</span>
                            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                                Scope-driven. Schedule-tight.
                            </h2>
                            <p className="text-white/60 mt-6 leading-relaxed">
                                Our commercial workmanship focuses on precision
                                and technical excellence, delivering superior
                                structural results for local businesses,
                                developers, and operators.
                            </p>
                        </FadeUp>
                    </div>
                    <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                        {SCOPES.map((s, i) => (
                            <FadeUp
                                key={s}
                                delay={i}
                                className="bg-[#0A0A0A] px-6 py-6 flex items-center gap-4"
                            >
                                <Building2
                                    size={18}
                                    className="text-[#F5A623] shrink-0"
                                />
                                <span className="text-sm text-white/80">
                                    {s}
                                </span>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* COMMERCIAL PROJECTS — gallery */}
            <section
                className="section-pad bg-[#080808] hairline-top"
                data-testid="commercial-portfolio-section"
            >
                <div className="container-bevel">
                    <FadeUp>
                        <span className="overline">Commercial Projects</span>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                            Selected commercial work.
                        </h2>
                    </FadeUp>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {photos.length > 0
                            ? photos.map((p) => (
                                  <PhotoCard photo={p} key={p.id} />
                              ))
                            : COMMERCIAL_GALLERY.map((g, i) => (
                                  <StockCard photo={g} key={i} />
                              ))}
                    </div>

                    {photos.length === 0 && (
                        <p
                            className="mt-4 text-[10px] uppercase tracking-[0.2em] text-white/40"
                            data-testid="commercial-gallery-disclosure"
                        >
                            Representative imagery
                        </p>
                    )}

                    <FadeUp delay={2}>
                        <div className="mt-10 flex justify-center">
                            <Link
                                to="/contact"
                                data-testid="commercial-portfolio-cta"
                                className="btn-secondary"
                            >
                                Request Project References{" "}
                                <ArrowRight size={14} />
                            </Link>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* GUARANTEE */}
            <section className="section-pad">
                <div className="container-bevel grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7">
                        <FadeUp>
                            <span className="overline">The Bevel Standard</span>
                            <h2 className="font-display text-3xl md:text-4xl mt-4 leading-tight">
                                One PM. One schedule. One point of accountability.
                            </h2>
                            <p className="text-white/60 mt-5 max-w-2xl leading-relaxed">
                                We prioritize reliable scheduling and clear
                                communication, keeping stakeholders informed at
                                every stage to ensure timely project delivery.
                            </p>
                        </FadeUp>
                    </div>
                    <FadeUp delay={1} className="md:col-span-5">
                        <div className="border border-white/10 bg-[#0A0A0A] p-8">
                            <ShieldCheck
                                size={28}
                                className="text-[#F5A623]"
                                strokeWidth={1.5}
                            />
                            <h3 className="font-display text-xl mt-5 text-white">
                                Built to spec. Delivered on plan.
                            </h3>
                            <p className="text-white/60 mt-3 text-sm">
                                Weekly stakeholder reporting, transparent
                                scheduling, and full-cycle oversight, so your
                                project finishes when it should.
                            </p>
                            <Link
                                to="/contact"
                                data-testid="commercial-guarantee-cta"
                                className="btn-primary mt-8"
                            >
                                Start a Project <ArrowRight size={14} />
                            </Link>
                        </div>
                    </FadeUp>
                </div>
            </section>
        </main>
    );
}

function PhotoCard({ photo }) {
    const src = photo.storage_path
        ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${photo.storage_path}`
        : "";
    return (
        <div className="bg-[#0A0A0A] group">
            <div className="relative aspect-[4/3] overflow-hidden">
                <img
                    src={src}
                    alt={photo.title || "Project photo"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
            </div>
            {(photo.title || photo.description) && (
                <div className="p-5 border-t border-white/10">
                    {photo.title && (
                        <div className="font-display text-lg text-white">
                            {photo.title}
                        </div>
                    )}
                    {photo.description && (
                        <div className="text-sm text-white/55 mt-1">
                            {photo.description}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function StockCard({ photo }) {
    return (
        <div className="bg-[#0A0A0A] group">
            <div className="relative aspect-[4/3] overflow-hidden">
                <img
                    src={photo.src}
                    alt={photo.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
            </div>
            <div className="p-5 border-t border-white/10">
                <div className="font-display text-lg text-white">
                    {photo.title}
                </div>
            </div>
        </div>
    );
}
